// GuestRowActions — the Guest table's Actions cell: a kebab that opens a
// Dropdown menu with Edit guest + Delete. Owns its own open state (one row's
// menu at a time; outside-click closes). The actual edit/delete flows live in
// PeoplePage via the onEdit/onDelete callbacks — both OPTIONAL: the page passes
// `undefined` when the verb is missing and the item doesn't render (absence
// means hidden). With neither grant the kebab has no items left (there is no
// read item here), so the cell renders nothing.

import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, IconButton } from '@/components/common';

interface GuestRowActionsProps {
  /** Absent = hidden. */
  onEdit?: () => void;
  /** Absent = hidden. */
  onDelete?: () => void;
}

const GuestRowActions = ({ onEdit, onDelete }: GuestRowActionsProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const stop = (e: MouseEvent) => e.stopPropagation();

  if (!onEdit && !onDelete) return null;

  return (
    <span className='people-table__actions' onClick={stop}>
      <Dropdown
        open={open}
        onClose={() => setOpen(false)}
        align='end'
        width={180}
        portal
        trigger={
          <IconButton
            icon='ellipsis-vertical'
            variant='ghost'
            size='sm'
            aria-label={t('people_col_actions')}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          />
        }
      >
        {onEdit && (
          <button
            type='button'
            className='app-dropdown__item'
            role='menuitem'
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            <Icon name='pencil' size={15} />
            <span className='app-dropdown__item-label'>{t('guest_menu_edit')}</span>
          </button>
        )}
        {onEdit && onDelete && <div className='app-dropdown__divider' role='separator' />}
        {onDelete && (
          <button
            type='button'
            className='app-dropdown__item app-dropdown__item--danger'
            role='menuitem'
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            <Icon name='trash-2' size={15} />
            <span className='app-dropdown__item-label'>{t('button_delete')}</span>
          </button>
        )}
      </Dropdown>
    </span>
  );
};

export default GuestRowActions;
